import { replyIsLive, type ErrorCode, type Reply, type SuggestionOutcome } from '@rp/contracts'
import { failureGuidance, type FailureGuidance } from './failure'

export type SendState =
  | { kind: 'idle' }
  | { kind: 'publishing'; already: boolean; replyId: string }
  | { kind: 'sent'; outcome: SuggestionOutcome | null; already: boolean }
  | { kind: 'rival'; reply: Reply }
  | { kind: 'failed'; guidance: FailureGuidance }

export type SendAttempt = { text: string; inReplyToMessageId: string | null; suggestionId: string | null }

export const IDLE_SEND_STATE: SendState = { kind: 'idle' }

const RIVAL_CODES: readonly ErrorCode[] = ['ALREADY_ANSWERED', 'REPLY_IN_PROGRESS']

export const stateForReply = (reply: Reply, already: boolean): SendState | null => {
  if (reply.status === 'published') return { kind: 'sent', outcome: reply.outcome, already }
  if (reply.status === 'publishing') return { kind: 'publishing', already, replyId: reply.id }
  return null
}

const answersSameMessage = (reply: Reply, attempt: SendAttempt): boolean =>
  reply.inReplyToMessageId === attempt.inReplyToMessageId

export type Refusal = { guidance: FailureGuidance; code: ErrorCode | null; userId: string }

const liveReplyWith = (replies: readonly Reply[], attempt: SendAttempt, userId: string): Reply | null =>
  replies.find(
    (reply) =>
      replyIsLive(reply.status) &&
      reply.postedBy === userId &&
      answersSameMessage(reply, attempt) &&
      reply.text === attempt.text.trim(),
  ) ?? null

const rivalReplyTo = (replies: readonly Reply[], attempt: SendAttempt): Reply | null =>
  replies.find(
    (reply) => reply.status === 'publishing' || (reply.status === 'published' && answersSameMessage(reply, attempt)),
  ) ?? null

const rivalState = (replies: readonly Reply[], attempt: SendAttempt, code: ErrorCode | null): SendState | null => {
  if (code === null || !RIVAL_CODES.includes(code)) return null
  const rival = rivalReplyTo(replies, attempt)
  return rival === null ? null : { kind: 'rival', reply: rival }
}

export const stateAfterRefusal = (
  replies: readonly Reply[],
  attempt: SendAttempt,
  refusal: Refusal,
): SendState => {
  const failed: SendState = { kind: 'failed', guidance: refusal.guidance }
  const live = liveReplyWith(replies, attempt, refusal.userId)
  if (live !== null) return stateForReply(live, true) ?? failed
  return rivalState(replies, attempt, refusal.code) ?? failed
}

export type RestoredSend = { state: SendState; text: string; suggestionId: string | null }

const EMPTY_RESTORE: RestoredSend = { state: IDLE_SEND_STATE, text: '', suggestionId: null }

export const restoredSend = (replies: readonly Reply[], inReplyToMessageId: string | null, userId: string): RestoredSend => {
  const own = replies.filter((reply) => reply.postedBy === userId && reply.inReplyToMessageId === inReplyToMessageId).at(-1)
  if (own === undefined) return EMPTY_RESTORE
  if (own.status === 'published') return { ...EMPTY_RESTORE, state: stateForReply(own, true) ?? IDLE_SEND_STATE }
  const state = stateForReply(own, true) ?? { kind: 'failed', guidance: failureGuidance(own.metaFailure, null) }
  return { state, text: own.text, suggestionId: own.suggestionId }
}

export const settledState = (state: SendState, replies: readonly Reply[]): SendState => {
  if (state.kind !== 'publishing') return state
  const tracked = replies.find((reply) => reply.id === state.replyId)
  if (tracked === undefined || tracked.status === 'publishing') return state
  return stateForReply(tracked, true) ?? { kind: 'failed', guidance: failureGuidance(tracked.metaFailure, null) }
}
